import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { generateWallet, importWallet, createWalletEntry, decryptWallet } from "../../services/wallet-service";
import { supabase } from "../../services/supabase-client";

const WalletManager = ({ isOpen, onClose, mode = 'manage', onRequestUnlock }) => {
  const { user, session, vault, vaultKey, isUnlocked, saveVault } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [privateKeyInput, setPrivateKeyInput] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importMode, setImportMode] = useState(mode === 'import');

  useEffect(() => {
    if (isOpen) {
      setImportMode(mode === 'import');
      setError(null); // Clear previous errors
      if (isUnlocked && vault && vaultKey) {
      fetchWallets();
      } else if (!user) {
        setError("Please sign in to manage wallets");
        setLoading(false);
      } else if (!isUnlocked) {
        setError("Please unlock your vault to manage wallets");
        setLoading(false);
      }
    }
  }, [isOpen, mode, user?.id, isUnlocked, vault?.wallets?.length]);

  const fetchWallets = useCallback(async () => {
    if (!isUnlocked || !vault || !vaultKey) {
      setError("Vault not unlocked");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    // Always start with vault wallets (client-side source of truth)
    const vaultWallets = vault.wallets || [];
    
    // Convert vault wallets to display format
    const vaultWalletsDisplay = vaultWallets.map(w => ({
      id: w.id,
      wallet_address: w.publicKey,
      publicKey: w.publicKey,
      is_primary: w.isPrimary,
      is_default: w.isDefault,
      created_at: w.createdAt,
    }));
    
    try {
      // Try to fetch wallet metadata from Supabase (optional - for syncing metadata)
      if (user?.id && session?.access_token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallets/list`, {
        method: "GET",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
      });

          if (response.ok) {
      const data = await response.json();
            if (data.success && data.wallets) {
              // Merge vault data with Supabase metadata
              const walletMap = new Map();
              vaultWallets.forEach(vw => walletMap.set(vw.publicKey, vw));
              
              const mergedWallets = (data.wallets || []).map(sbWallet => {
                const vaultWallet = walletMap.get(sbWallet.wallet_address);
                if (!vaultWallet) return null; // Skip wallets not in vault
                return {
                  id: sbWallet.id,
                  wallet_address: sbWallet.wallet_address,
                  publicKey: sbWallet.wallet_address,
                  is_primary: sbWallet.is_primary,
                  is_default: sbWallet.is_default,
                  created_at: sbWallet.created_at,
                };
              }).filter(w => w !== null); // Remove null entries
              
              // If merge succeeded, use merged data, otherwise fallback to vault
              if (mergedWallets.length > 0 || vaultWallets.length === 0) {
                setWallets(mergedWallets);
                setLoading(false);
                return;
              }
            }
          }
        } catch (fetchError) {
          // Supabase fetch failed - non-critical, just log and use vault wallets
          console.warn("Failed to fetch wallet metadata from Supabase (using vault data):", fetchError);
      }
      }
      
      // Use vault wallets as fallback (always works even if Supabase is down)
      setWallets(vaultWalletsDisplay);
    } catch (err) {
      console.error("Error processing wallets:", err);
      // Even on error, show vault wallets (they're the source of truth)
      setWallets(vaultWalletsDisplay);
      // Only show error if we have no wallets at all
      if (vaultWallets.length === 0) {
        setError("Failed to load wallets. Please try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  }, [isUnlocked, vault, vaultKey, user?.id, session?.access_token]);

  const handleImportWallet = async () => {
    if (!isUnlocked || !vaultKey || !vault) {
      setError("Vault not unlocked");
      return;
    }

    if (!privateKeyInput.trim()) {
      setError("Please enter a private key");
      return;
    }

    setIsImporting(true);
    setError(null);
    setSuccess(null);

    try {
      // Import wallet client-side
      const keypair = importWallet(privateKeyInput.trim());
      const publicKey = keypair.publicKey.toString();
      
      // Check if wallet already exists in vault
      const existingWallet = (vault.wallets || []).find(w => w.publicKey === publicKey);
      if (existingWallet) {
        setError("Wallet already imported");
        setIsImporting(false);
        return;
      }
      
      // Check wallet limit (max 21)
      if ((vault.wallets || []).length >= 21) {
        setError("Maximum wallet limit reached (21 wallets)");
        setIsImporting(false);
        return;
      }
      
      // Create wallet entry with encryption
      const isFirstWallet = (vault.wallets || []).length === 0;
      const walletEntry = await createWalletEntry(keypair, vaultKey, {
        isPrimary: isFirstWallet,
        isDefault: isFirstWallet,
      });
      
      // Add to vault
      const updatedVault = {
        ...vault,
        wallets: [...(vault.wallets || []), walletEntry]
      };
      
      // Save vault (updates context state automatically)
      const saveResult = await saveVault(updatedVault);
      if (!saveResult.success) {
        throw new Error(saveResult.error || "Failed to save wallet");
      }
      
      // Sync to Supabase (store metadata only - public key, isPrimary, etc.)
      if (user?.id && session?.access_token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallets/import`, {
        method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
        body: JSON.stringify({
              walletAddress: publicKey,
              isPrimary: isFirstWallet,
              isDefault: isFirstWallet,
        }),
      });

          // Don't fail if Supabase sync fails - vault is the source of truth
          if (!response.ok) {
            console.warn("Failed to sync wallet metadata to Supabase (non-critical):", "Network error");
          }
        } catch (syncErr) {
          console.warn("Failed to sync wallet metadata to Supabase (non-critical):", syncErr.message || "Network error");
        }
      }
      
      // Update local state immediately (optimistic update)
      setWallets(updatedVault.wallets.map(w => ({
        id: w.id,
        wallet_address: w.publicKey,
        publicKey: w.publicKey,
        is_primary: w.isPrimary,
        is_default: w.isDefault,
        created_at: w.createdAt,
      })));
      
        setSuccess("Wallet imported successfully!");
        setPrivateKeyInput("");
      // No need to fetchWallets - we already updated the state above
        setTimeout(() => {
          setImportMode(false);
          setSuccess(null);
        }, 2000);
    } catch (err) {
      console.error("Error importing wallet:", err);
      setError(err.message || "Failed to import wallet");
    } finally {
      setIsImporting(false);
    }
  };

  const handleSetPrimary = async (walletId) => {
    if (!isUnlocked || !vault || !vaultKey) {
      setError("Vault not unlocked");
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      // Update vault: set all wallets to not primary, then set selected one as primary
      const updatedWallets = (vault.wallets || []).map(w => ({
        ...w,
        isPrimary: w.id === walletId
      }));
      
      const updatedVault = {
        ...vault,
        wallets: updatedWallets
      };
      
      const saveResult = await saveVault(updatedVault);
      if (!saveResult.success) {
        throw new Error(saveResult.error || "Failed to update vault");
      }
      
      // Sync to Supabase
      if (user?.id && session?.access_token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallets/set-primary`, {
        method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
        body: JSON.stringify({
          walletId: walletId
        }),
      });
          // Don't fail if Supabase sync fails
          if (!response.ok) {
            console.warn("Failed to sync primary wallet to Supabase");
          }
        } catch (syncErr) {
          console.warn("Error syncing to Supabase:", syncErr);
        }
      }
      
      // Update local state immediately (optimistic update)
      setWallets(updatedVault.wallets.map(w => ({
        id: w.id,
        wallet_address: w.publicKey,
        publicKey: w.publicKey,
        is_primary: w.isPrimary,
        is_default: w.isDefault,
        created_at: w.createdAt,
      })));
      
      setSuccess("Primary wallet updated!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error("Error setting primary wallet:", err);
      setError(err.message || "Failed to set primary wallet");
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!isUnlocked || !vault || !vaultKey) {
      setError("Vault not unlocked");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this wallet? This action cannot be undone.")) {
      return;
    }

    // Check wallet count
    if ((vault.wallets || []).length <= 1) {
      setError("Cannot delete your only wallet");
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      // Remove wallet from vault
      const walletToDelete = (vault.wallets || []).find(w => w.id === walletId);
      if (!walletToDelete) {
        setError("Wallet not found");
        return;
      }
      
      const updatedWallets = (vault.wallets || []).filter(w => w.id !== walletId);
      
      // If deleted wallet was primary, set first remaining wallet as primary
      if (walletToDelete.isPrimary && updatedWallets.length > 0) {
        updatedWallets[0].isPrimary = true;
      }
      
      const updatedVault = {
        ...vault,
        wallets: updatedWallets
      };
      
      const saveResult = await saveVault(updatedVault);
      if (!saveResult.success) {
        throw new Error(saveResult.error || "Failed to update vault");
      }
      
      // Sync to Supabase
      if (user?.id && session?.access_token) {
    try {
      const response = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallets/delete`, {
        method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
        body: JSON.stringify({
          walletId: walletId
        }),
      });
          // Don't fail if Supabase sync fails
          if (!response.ok) {
            console.warn("Failed to sync wallet deletion to Supabase");
          }
        } catch (syncErr) {
          console.warn("Error syncing to Supabase:", syncErr);
        }
      }
      
      // Update local state immediately (optimistic update)
      setWallets(updatedVault.wallets.map(w => ({
        id: w.id,
        wallet_address: w.publicKey,
        publicKey: w.publicKey,
        is_primary: w.isPrimary,
        is_default: w.isDefault,
        created_at: w.createdAt,
      })));
      
        setSuccess("Wallet deleted successfully!");
        setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error("Error deleting wallet:", err);
      setError(err.message || "Failed to delete wallet");
    }
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-[#0a0e1a] p-6 rounded-lg shadow-lg border border-[rgba(255,255,255,0.1)] w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Wallet Manager</h3>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Toggle between Manage and Import */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setImportMode(false)}
              className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                !importMode
                  ? "bg-blue-500 text-white"
                  : "bg-white/10 text-white/70 hover:bg-white/15"
              }`}
            >
              Manage Wallets ({(vault?.wallets?.length || wallets.length)}/21)
            </button>
            <button
              onClick={() => setImportMode(true)}
              className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                importMode
                  ? "bg-blue-500 text-white"
                  : "bg-white/10 text-white/70 hover:bg-white/15"
              }`}
            >
              Import Wallet
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
              <div className="flex items-center justify-between gap-3">
                <span className="text-red-400 text-sm flex-1">{error}</span>
                {!user && onRequestUnlock && (
                  <button
                    onClick={() => {
                      onClose();
                      onRequestUnlock();
                    }}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors whitespace-nowrap"
                  >
                    Sign In
                  </button>
                )}
                {user && !isUnlocked && onRequestUnlock && (
                  <button
                    onClick={() => {
                      onClose();
                      onRequestUnlock();
                    }}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors whitespace-nowrap"
                  >
                    Unlock Vault
                  </button>
                )}
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-sm">
              {success}
            </div>
          )}

          {importMode ? (
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-xs font-bold">
                  ⚠️ WARNING: Your private key is encrypted client-side and stored locally. Only import keys for wallets you use exclusively for trading. Never share your private key with anyone.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Private Key (Base58)
                </label>
                <input
                  type="password"
                  value={privateKeyInput}
                  onChange={(e) => setPrivateKeyInput(e.target.value)}
                  placeholder="Enter your wallet private key"
                  className="w-full bg-white/10 text-white p-3 rounded-md mb-3 focus:outline-none border border-[rgba(255,255,255,0.1)] focus:border-[#4878ff] focus:ring-2 focus:ring-[#4878ff]/20 placeholder-white/40"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleImportWallet();
                    }
                  }}
                />
                <button
                  onClick={handleImportWallet}
                  disabled={isImporting || !privateKeyInput.trim() || !isUnlocked || (vault?.wallets?.length || 0) >= 21}
                  className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white rounded-md transition-colors font-medium"
                >
                  {isImporting ? "Importing..." : !isUnlocked ? "Unlock vault first" : (vault?.wallets?.length || 0) >= 21 ? "Maximum wallets reached (21)" : "Import Wallet"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-white/60">Loading wallets...</div>
              ) : wallets.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  <p>No wallets found. Import a wallet to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {wallets.map((wallet) => (
                    <div
                      key={wallet.id}
                      className={`p-4 rounded-lg border ${
                        wallet.is_primary
                          ? "bg-blue-500/10 border-blue-500/30"
                          : "bg-white/5 border-white/10"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">
                              {formatAddress(wallet.wallet_address)}
                            </span>
                            {wallet.is_primary && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                PRIMARY
                              </span>
                            )}
                            {wallet.is_default && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                DEFAULT
                              </span>
                            )}
                          </div>
                          <p className="text-white/60 text-xs">
                            Created: {new Date(wallet.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {!wallet.is_primary && (
                            <button
                              onClick={() => handleSetPrimary(wallet.id)}
                              className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs rounded transition-colors"
                            >
                              Set Primary
                            </button>
                          )}
                          {wallets.length > 1 && (
                            <button
                              onClick={() => handleDeleteWallet(wallet.id)}
                              className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default WalletManager;

